import dayjs from "dayjs";
import * as echarts from "echarts";
import React, { useEffect, useMemo, useRef } from "react";

import { StageAnalytics, StageMeta, StageOverall } from "@/types/job";

import { colorForKey, labelForKey } from "./utils/helpers";
import { OverviewCard } from "./utils/OverviewCards";

export type Props = {
  data: StageAnalytics;
  interval: string;
};

const useStageMeta = (overall: StageOverall): StageMeta[] =>
  useMemo(
    () =>
      Object.keys(overall).map((key) => ({
        key,
        label: labelForKey(key),
        color: colorForKey(key),
      })),
    [overall],
  );

const ReusableStackedStageChart: React.FC<Props> = ({ data, interval }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const chart = useRef<echarts.ECharts | null>(null);
  const stages = useStageMeta(data.overall);

  const intervalData = data[interval] || [];

  useEffect(() => {
    if (!ref.current || chart.current) return;
    chart.current = echarts.init(ref.current);
    return () => chart.current?.dispose();
  }, []);

  const option = useMemo<echarts.EChartsOption>(() => {
    if (!intervalData.length || !stages.length) return {};

    const xAxisLabels = intervalData.map((d) => {
      if (interval === "daily" && d.date) {
        return dayjs(d.date).format("DD MMM");
      } else if (interval === "weekly" && d.week) {
        const match = d.week.match(/\((.+) to (.+)\)/);
        if (match) {
          const start = dayjs(match[1]).format("DD MMM");
          const end = dayjs(match[2]).format("DD MMM");
          return `${start}-${end}`;
        }
        return d.week;
      }
      return "";
    });

    return {
      animation: false,
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: {
        bottom: 0,
        left: 0,
        padding: [0, 0, 0, 20],
      },
      grid: { top: 20, left: 40, right: 20, bottom: 80, containLabel: true },
      xAxis: {
        type: "category",
        data: xAxisLabels,
        axisLabel: {
          rotate: 0,
          interval: 0,
          formatter: (value: string) => {
            if (interval === "weekly" && value.includes("-")) {
              const [start, end] = value.split("-");
              return `${start}\n${end}`;
            }
            return value;
          },
        },
      },
      yAxis: { type: "value" },
      series: stages.map((stage) => ({
        name: stage.label,
        type: "bar",
        stack: "total",
        barWidth: 24,
        itemStyle: { color: stage.color },
        data: intervalData.map((d) => d[stage.key] ?? 0),
      })),
    };
  }, [intervalData, stages, interval]);

  useEffect(() => {
    if (intervalData.length && stages.length) {
      chart.current?.setOption(option, true);
    } else {
      chart.current?.clear();
    }
  }, [option, intervalData.length, stages.length]);

  useEffect(() => {
    if (!ref.current || !chart.current) return;
    const ro = new ResizeObserver(() => chart.current?.resize());
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  if (!intervalData.length || !stages.length) {
    return (
      <div className="w-full py-20 text-center text-gray-500">
        No data available
      </div>
    );
  }

  return (
    <div className="w-full">
      <OverviewCard stages={stages} overall={data.overall} />
      <div className="w-full overflow-x-auto p-2">
        <div
          ref={ref}
          style={{ height: 360, minWidth: intervalData.length * 60 }}
        />
      </div>
    </div>
  );
};

export default ReusableStackedStageChart;
